/**
 * Client-side 404 reporter.
 *
 * Two flavors:
 *   - logPage404(url)   — fired from app/not-found.tsx when Next.js renders
 *                         the not-found UI for a missing page.
 *   - logAsset404(src)  — fired from ErrorReporter's capture-phase error
 *                         listener when an <img>/<script>/<link> fails to load.
 *
 * Both POST to the existing /api/errors/ingest endpoint with status_code=404
 * so they land in the same Bug Board the app errors do, just under a separate
 * status_code filter.
 *
 * In-memory dedup so a page full of broken images doesn't flood ingest.
 */

import { detectBotUserAgent } from '@/lib/errorEnrichment';

// Patterns that are 99%+ malicious — bot probes for common exploits/credentials.
// When a 404 hits a path matching these, we tag context.attack=true so the
// Bug Board can filter "real customer 404s" vs "noise from attackers".
const ATTACK_PROBE_PATTERNS = [
  /\/wp-admin/i,
  /\/wp-login/i,
  /\/wp-content/i,
  /\/wp-includes/i,
  /\/xmlrpc\.php/i,
  /\/phpmyadmin/i,
  /\/\.env(\.|$)/i,
  /\/\.git\b/i,
  /\/\.svn\b/i,
  /\/\.aws\b/i,
  /\/\.ssh\b/i,
  /\/config\.json/i,
  /\/composer\.(lock|json)/i,
  /\/owa\b/i,
  /\/ecp\b/i,
  /\/manager\/html/i,
  /\/cgi-bin\b/i,
  /\/struts/i,
  /\/jenkins\b/i,
  /\/druid\b/i,
  /\/actuator\b/i,
  /\/metadata\b/i,
  /\/console\b/i,
];

export function isSuspiciousProbe(urlOrPath: string): { attack: boolean; pattern?: string } {
  for (const p of ATTACK_PROBE_PATTERNS) {
    if (p.test(urlOrPath)) return { attack: true, pattern: p.source };
  }
  return { attack: false };
}

// Dedup: same (kind, normalized-url) within window = drop.
const recentlySent = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;

/**
 * Paths we put in outbound email, SMS or printed material.
 *
 * A 404 on one of these is always our fault and always high-impact — the
 * customer cannot self-serve and usually cannot even tell us what broke. They
 * are exempt from the internal-referrer rule because an emailed link, by
 * definition, never has an internal referrer.
 */
const TRANSACTIONAL_PATHS = [
  /^\/reset-password/i,
  /^\/forgot-password/i,
  /^\/verify-email/i,
  /^\/track-order/i,
  /^\/cart\/recover\//i,
  /^\/account\//i,
  /^\/orders?\//i,
  /^\/products\/configure\//i,
  /^\/unsubscribe/i,
];

export function isTransactionalPath(urlOrPath: string): boolean {
  try {
    const path = urlOrPath.startsWith('http')
      ? new URL(urlOrPath).pathname
      : urlOrPath.split('?')[0];
    return TRANSACTIONAL_PATHS.some((p) => p.test(path));
  } catch {
    return false;
  }
}

function dedupKey(kind: string, url: string): string {
  // Strip query strings so /foo?utm=a and /foo?utm=b dedupe together.
  return `${kind}:${url.split('?')[0]}`;
}

function recentlySentKey(key: string): boolean {
  const now = Date.now();
  for (const [k, ts] of recentlySent) {
    if (now - ts > DEDUP_WINDOW_MS) recentlySent.delete(k);
  }
  if (recentlySent.has(key)) return true;
  recentlySent.set(key, now);
  return false;
}

async function send(payload: Record<string, unknown>): Promise<void> {
  try {
    await fetch('/api/errors/ingest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* never throw from a logger */
  }
}

/**
 * Log a page-level 404 (Next.js rendered not-found.tsx).
 * Tags context.attack=true if the URL matches a known probe pattern.
 * Skips clearly-bot UAs unless it's an attack-probe (we always want to
 * see attack patterns even from bots — that's signal, not noise).
 */
export async function logPage404(url: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const key = dedupKey('page404', url);
  if (recentlySentKey(key)) return;

  const probe = isSuspiciousProbe(url);
  const bot = detectBotUserAgent(navigator.userAgent);

  // Real customers don't guess URLs; bots do. Skip page 404s from
  // clearly-bot UAs unless the URL is an attack probe (those we always
  // want to know about — even from bots). Reduces bug board noise from
  // crawler-fabricated /blog/<random-slug> and /<some-guess> entries.
  const isBot = bot.isBot || looksLikeBot(navigator.userAgent);
  if (isBot && !probe.attack) return;

  // Only report ACTIONABLE 404s: ones reached by clicking a link ON our own
  // site (a broken internal link worth fixing). 404s with no referrer or an
  // external referrer are typed URLs / old external backlinks / crawlers with
  // browser-like UAs — not our bug — so we skip them to keep the board clean.
  // Attack probes are still logged (they're security signal, not noise).
  let internalReferrer = false;
  try {
    const ref = document.referrer;
    internalReferrer = !!ref && new URL(ref).origin === window.location.origin;
  } catch { /* malformed referrer → treat as external */ }
  // ...but a link we emailed the customer arrives with an EXTERNAL referrer (or
  // none — Gmail strips it), so the rule above hid the worst 404s we can have.
  // A dead /reset-password link went unreported for two months this way while
  // eight customers were unable to set a password. Links we send out are always
  // our bug, so they bypass the referrer test.
  if (!probe.attack && !internalReferrer && !isTransactionalPath(url)) return;

  await send({
    source: 'client',
    statusCode: 404,
    message: probe.attack
      ? `Suspicious 404 probe: ${url}`
      : `Page 404: ${url}`,
    url,
    userAgent: navigator.userAgent,
    release: (window as any).__APP_RELEASE__ || null,
    context: {
      kind: 'page-404',
      attack: probe.attack || undefined,
      attackPattern: probe.pattern || undefined,
      referrer: document.referrer || null,
      bot: bot.isBot || isBot || undefined,
      botLabel: bot.label || undefined,
    },
  });
}

// Session-level burst counter. Real customers don't fail 5+ asset loads
// in 10 seconds — but bots scanning a homepage do. After threshold, we
// stop logging for the rest of the session window. This catches
// sophisticated bots whose UA looks like a normal browser (Playwright on
// Linux X11 Chrome, headless Firefox without "Headless" in UA, etc.).
let burstCount = 0;
let burstWindowStart = 0;
const BURST_WINDOW_MS = 10_000;
const BURST_THRESHOLD = 5;

function inBurst(): boolean {
  const now = Date.now();
  if (now - burstWindowStart > BURST_WINDOW_MS) {
    burstWindowStart = now;
    burstCount = 1;
    return false;
  }
  burstCount++;
  return burstCount > BURST_THRESHOLD;
}

/**
 * Log an asset load failure (image/script/stylesheet — could be 404, 5xx,
 * network reset, cancelled load, ad blocker, slow timeout, etc.). The
 * browser's resource-error event does NOT expose the actual HTTP status,
 * so we deliberately do NOT claim it's a 404; status_code is left null.
 * Captured from window's error event in capture phase (see ErrorReporter.tsx).
 */
export async function logAsset404(src: string, tagName: string): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!src) return;

  // Skip non-app assets (browser extensions, third-party CDNs we don't control)
  try {
    const url = new URL(src, window.location.origin);
    if (url.host !== window.location.host) return;
  } catch {
    return;
  }

  // Skip clearly-bot UAs — outdated Chrome versions and headless tools fire
  // these errors constantly because they don't actually load images.
  if (looksLikeBot(navigator.userAgent)) return;

  // Burst suppression: real customers don't fail 5+ asset loads in 10s.
  // Bot scanning a homepage does. Past threshold, stop logging.
  if (inBurst()) return;

  const key = dedupKey('asset', src);
  if (recentlySentKey(key)) return;

  // The resource-error event doesn't expose the HTTP status, so the failure
  // could be a real 404, a transient network blip, a cancelled load, or a 403
  // from bot-management/WAF. Probe with HEAD before reporting:
  //   - 2xx → the asset serves fine; original failure was a flake. Suppress.
  //   - 403 → bot wall blocked this client. Not actionable. Suppress.
  //   - other 4xx/5xx → real broken asset. Report.
  //   - network error → still unreachable. Report.
  try {
    const probe = await fetch(src, { method: 'HEAD', cache: 'no-store', credentials: 'omit' });
    if (probe.ok || probe.status === 403) return;
  } catch {
    // fall through and report
  }

  await send({
    source: 'client',
    statusCode: null,
    message: `Asset load failed (${tagName}): ${src}`,
    url: window.location.href,
    userAgent: navigator.userAgent,
    release: (window as any).__APP_RELEASE__ || null,
    context: {
      kind: 'asset-load-failed',
      assetUrl: src,
      assetTag: tagName,
      pageUrl: window.location.pathname,
    },
  });
}

/**
 * Heuristic bot UA detection for the asset-error path.
 * NOT a security boundary — just noise reduction for the bug board.
 *
 * Catches:
 *   - Explicit bot/scraper signatures
 *   - Outdated Chrome (<100 — real users autoupdate)
 *   - Linux X11 Chrome — <0.5% of real US consumer traffic, but the
 *     default UA for Playwright/Puppeteer/headless tools on Linux servers
 *     (the smartblindsexpert.com burst of 17 hits in 10s was exactly this)
 *   - Headless detection signals exposed by the browser itself
 *     (navigator.webdriver, missing window.chrome on Chrome UA, etc.)
 */
function looksLikeBot(ua: string): boolean {
  if (!ua) return false;

  // Explicit bot/scraper signatures
  if (/bot\b|crawler|spider|scrape|headless|phantom|playwright|puppeteer|selenium|curl|wget/i.test(ua)) {
    return true;
  }

  // Forged Chrome with an implausibly-old version (real users autoupdate).
  const chromeMatch = ua.match(/Chrome\/(\d+)/i);
  if (chromeMatch && parseInt(chromeMatch[1], 10) < 100) return true;

  // Linux X11 Chrome — overwhelmingly Playwright/Puppeteer/automation tooling.
  // Real desktop Linux users in our customer demographic are vanishingly rare.
  if (/X11; Linux/i.test(ua) && /Chrome\//i.test(ua)) return true;

  // Linux Firefox without "Ubuntu"/"Fedora" branding — usually a headless rig.
  // (Real Firefox-on-Linux UAs usually include the distro string.)
  if (/X11; Linux/i.test(ua) && /Firefox\//i.test(ua) && !/Ubuntu|Fedora|Debian|SUSE/i.test(ua)) return true;

  // Browser-exposed automation flags (when accessible from within the page)
  try {
    if ((navigator as any).webdriver === true) return true;
    // Headless Chrome doesn't populate window.chrome
    if (/Chrome\//i.test(ua) && typeof (window as any).chrome === 'undefined') return true;
  } catch { /* ignore */ }

  return false;
}

// ---------------------------------------------------------------------------
// API failure reporting
// ---------------------------------------------------------------------------

/**
 * Endpoints whose 4xx responses are normal application flow, not defects.
 *
 * `auth/me` 401s on every anonymous page view; login 401s on a typo'd password;
 * coupon validation 400s whenever someone mistypes a code. Reporting those
 * would bury the real breakages.
 */
const EXPECTED_API_FAILURES: Array<{ pattern: RegExp; statuses: number[] }> = [
  { pattern: /\/api\/v2\/auth\/me\b/i, statuses: [401, 403] },
  { pattern: /\/api\/v2\/auth\/login\b/i, statuses: [400, 401] },
  { pattern: /\/api\/v2\/auth\/register\b/i, statuses: [400, 409] },
  { pattern: /\/api\/v2\/auth\/forgot-password\b/i, statuses: [429] },
  { pattern: /\/api\/v2\/auth\/reset-password\b/i, statuses: [400] },
  { pattern: /\/api\/v2\/commerce\/cart\/apply-coupon\b/i, statuses: [400] },
  { pattern: /\/api\/errors\/ingest\b/i, statuses: [400, 401, 403, 404, 429, 500] },
];

function isExpectedApiFailure(url: string, status: number): boolean {
  return EXPECTED_API_FAILURES.some(
    (e) => e.pattern.test(url) && e.statuses.includes(status)
  );
}

/**
 * Report an API call that came back 4xx/5xx.
 *
 * This is the gap that let ~20 dead endpoints run unnoticed. A `fetch()` that
 * resolves with `ok: false` throws nothing, rejects nothing and loads no
 * resource, so none of the three listeners in ErrorReporter can see it — the
 * calling code usually swallows it and the customer just sees a feature that
 * silently does nothing (dead search autocomplete, an empty installer
 * dashboard, a password page that never existed).
 */
export async function logApiFailure(params: {
  url: string;
  status: number;
  method: string;
  pageUrl: string;
}): Promise<void> {
  if (typeof window === 'undefined') return;
  const { url, status, method, pageUrl } = params;

  if (status < 400) return;
  // 401 = signed out or session expired; portal layouts redirect to login.
  // The v2 route doesn't log 401s server-side for the same reason.
  if (status === 401) return;
  if (isExpectedApiFailure(url, status)) return;

  // Bots hitting dead endpoints tell us nothing about customer experience.
  if (detectBotUserAgent(navigator.userAgent).isBot || looksLikeBot(navigator.userAgent)) return;

  // Dedup on route + status, not the full URL, so one broken endpoint reports
  // once rather than once per query string.
  let routeKey = url;
  try {
    routeKey = new URL(url, window.location.origin).pathname;
  } catch { /* keep the raw string */ }
  const key = dedupKey('api', `${method}:${routeKey}:${status}`);
  if (recentlySentKey(key)) return;

  await send({
    source: 'client',
    statusCode: status,
    message: `API ${status} on ${method} ${routeKey}`,
    url: routeKey,
    method,
    userAgent: navigator.userAgent,
    release: (window as any).__APP_RELEASE__ || null,
    context: {
      kind: 'api-failure',
      requestUrl: url,
      pageUrl,
      // A 404 means the route does not exist at all — almost always a stale
      // path in the frontend rather than a transient failure.
      likelyDeadEndpoint: status === 404 || status === 405,
    },
  });
}
